// Mirror of supabase/functions/_shared/decoding.ts pure logic, for the in-browser tester.
// Keep in sync with the edge function decoders.

const monthNames = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

function weekToMonth(week: number): string {
  const approxMonth = Math.ceil(week / 4.33);
  return monthNames[Math.min(approxMonth - 1, 11)] || 'Unknown';
}

function formatProductionDate(decoded: any): string | null {
  if (!decoded) return null;
  if (decoded.year && decoded.month) return `${monthNames[decoded.month - 1]} ${decoded.year}`;
  if (decoded.year && decoded.week)  return `${weekToMonth(decoded.week)} ${decoded.year} (Week ${decoded.week})`;
  if (decoded.year) return `${decoded.year}`;
  return null;
}

function decodeBSH_FD(fd: string) {
  if (!fd || fd.length !== 4) return null;
  const yc = parseInt(fd.substring(0,2)); const m = parseInt(fd.substring(2,4));
  if (isNaN(yc) || isNaN(m) || m<1 || m>12) return null;
  return { year: 2000 + ((yc + 20) % 100), month: m };
}
// Shared decade resolver: pick the most recent plausible year not in the future.
export function resolveDecadeYear(digit: number, now: number = new Date().getFullYear()): number {
  const later = 2020 + digit;
  const earlier = 2010 + digit;
  if (later <= now) return later;
  if (earlier <= now) return earlier;
  return earlier;
}
function decodeElectrolux_SNo(s: string) {
  if (!s || s.length<3) return null;
  const y = parseInt(s.charAt(0)); const w = parseInt(s.substring(1,3));
  if (isNaN(y)||isNaN(w)||w<1||w>53) return null;
  return { year: resolveDecadeYear(y), week: w };
}
function decodeSamsung_SN(sn: string) {
  if (!sn || sn.length<9) return null;
  // Samsung historically skips F,G,H,I,O,P,Q,S,U,V,Z. Extended past 2023 best-effort.
  const yMap: Record<string,number> = {A:2010,B:2011,C:2012,D:2013,E:2014,K:2016,M:2017,N:2018,R:2019,T:2020,W:2021,X:2022,Y:2023,J:2024,L:2025};
  const mMap: Record<string,number> = {'1':1,'2':2,'3':3,'4':4,'5':5,'6':6,'7':7,'8':8,'9':9,'A':10,'B':11,'C':12};
  return { year: yMap[sn.charAt(7).toUpperCase()], month: mMap[sn.charAt(8).toUpperCase()] };
}
function decodeWhirlpool_SN(sn: string) {
  if (!sn || sn.length<4) return null;
  const f = sn.charAt(0); const y = parseInt(sn.charAt(1)); const w = parseInt(sn.substring(2,4));
  if (isNaN(y)||isNaN(w)||w<1||w>53) return null;
  return { year: resolveDecadeYear(y), week: w, factory: f };
}
function decodeLG_SN(sn: string) {
  if (!sn || sn.length<3) return null;
  const y = parseInt(sn.charAt(0)); const m = parseInt(sn.substring(1,3));
  if (isNaN(y)||isNaN(m)||m<1||m>12) return null;
  return { year: resolveDecadeYear(y), month: m };
}
function decodeBeko_Serial(s: string) {
  if (!s || s.length<2) return null;
  const yc = parseInt(s.substring(0,2));
  if (isNaN(yc)) return null;
  return { year: 2000 + yc };
}

export interface DisambigRule { brand: string; pattern: string; applies_to: string; priority: number; active?: boolean; notes?: string | null; }
export interface FieldAlias  { brand: string; alias: string; canonical_field: string; active?: boolean; }
export interface DateDecoder {
  brand: string; kind: string; source_field: string | null;
  date_code_template: string | null; date_note: string | null;
  config: any;
  active?: boolean;
}
export interface DecodingRules { disambig: DisambigRule[]; aliases: FieldAlias[]; decoders: DateDecoder[]; }

export function applyBrandDisambiguation(extracted: any, rules: DecodingRules): any {
  const brand = String(extracted.brand || '').trim().toUpperCase();
  if (!brand) return extracted;
  // Every active rule row is self-scoped by its own brand/pattern/applies_to.


  const fieldOf = (r: DisambigRule) => {
    const v = r.applies_to === 'product' ? extracted.productNumber
            : r.applies_to === 'serial'  ? extracted.serialNumber
            : extracted.modelNumber;
    return String(v || '').toUpperCase().replace(/\s+/g,'');
  };
  const sorted = [...rules.disambig].sort((a,b) => b.priority - a.priority);
  for (const r of sorted) {
    const t = fieldOf(r); if (!t) continue;
    let re: RegExp;
    try { re = new RegExp(r.pattern); } catch { continue; }
    if (re.test(t)) {
      if (r.brand.toUpperCase() !== brand) extracted = { ...extracted, brand: r.brand };
      return extracted;
    }
  }
  return extracted;
}

function renderTemplate(tpl: string | null, data: any): string | null {
  if (!tpl) return null;
  return tpl.replace(/\{(\w+)\}/g, (_m, k) => (data[k] ?? '')).trim() || null;
}

export function decodeProductionDate(extracted: any, rules: DecodingRules) {
  const b = String(extracted.brand || '').toUpperCase();
  if (!b) return { productionDate: null, dateCode: null, dateNote: null };
  const decoder = rules.decoders.find(d => d.brand.toUpperCase() === b)
              ?? rules.decoders.find(d => b.includes(d.brand.toUpperCase()));
  if (!decoder) return { productionDate: null, dateCode: null, dateNote: null };

  const sourceVal = decoder.source_field ? String(extracted[decoder.source_field] ?? '') : '';
  let decoded: any = null;

  switch (decoder.kind) {
    case 'bsh_fd':         decoded = decodeBSH_FD(sourceVal.replace(/\D/g,'')); break;
    case 'electrolux_sno': decoded = decodeElectrolux_SNo(sourceVal); break;
    case 'samsung_sn':     decoded = decodeSamsung_SN(sourceVal); break;
    case 'whirlpool_sn':   decoded = decodeWhirlpool_SN(sourceVal); break;
    case 'lg_sn':          decoded = decodeLG_SN(sourceVal); break;
    case 'beko_serial':    decoded = decodeBeko_Serial(sourceVal); break;
    case 'position': {
      const c = decoder.config || {};
      const yp = Number(c.year_pos), mp = Number(c.month_pos), base = Number(c.year_base ?? 2000);
      if (sourceVal && Number.isFinite(yp)) {
        const y = parseInt(sourceVal.charAt(yp));
        const m = Number.isFinite(mp) ? parseInt(sourceVal.charAt(mp)) : undefined;
        if (!isNaN(y)) decoded = { year: base + y, month: !isNaN(m as number) ? m : undefined };
      }
      break;
    }
    case 'regex': {
      const c = decoder.config || {};
      try {
        const re = new RegExp(c.pattern);
        const m = sourceVal.match(re);
        if (m?.groups) {
          const yr = m.groups.year ? parseInt(m.groups.year) : undefined;
          const mo = m.groups.month ? parseInt(m.groups.month) : undefined;
          const wk = m.groups.week ? parseInt(m.groups.week) : undefined;
          const base = Number(c.year_base ?? 2000);
          if (yr !== undefined) decoded = { year: yr < 100 ? base + yr : yr, month: mo, week: wk };
        }
      } catch {}
      break;
    }
    default: decoded = null;
  }

  return {
    productionDate: formatProductionDate(decoded),
    dateCode: renderTemplate(decoder.date_code_template, extracted),
    dateNote: decoder.date_note,
  };
}
